// components/sections/GapAnalysisSection.js - VERSION PRODUCTION
import { ArrowUpDown, Filter, Grid3X3, List, Plus, Target, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import ObjectivesModal from '../../modals/objectif/ObjectivesModal';
import ActionPlanModal from '../../modals/plan-action/ActionPlanModal';
import GapAnalysisFilters from './GapAnalysisFilters';
import GapAnalysisItems from './GapAnalysisItems';

const GapAnalysisSection = ({ 
    data,
    hooks,
    onGlobalRefresh,
    onSmartRefresh,
    onCreateObjective,
    onUpdateObjective,
    onDeleteObjective
}) => {
    // Utiliser les données centralisées
    const {
        objectifs = [],
        plans = [],
        isLoading = false,
        hasError = false,
        errors = {}
    } = data || {};

    const {
        objectifsCachedHook,
        plansCachedHook
    } = hooks || {};

    // États locaux pour l'interface
    const [viewMode, setViewMode] = useState('card');
    const [showFilters, setShowFilters] = useState(false);
    const [showSort, setShowSort] = useState(false);
    const [sortBy, setSortBy] = useState('category');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filterBy, setFilterBy] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [showActionPlanModal, setShowActionPlanModal] = useState(false);
    const [editingObjective, setEditingObjective] = useState(null);
    const [selectedObjectiveForPlan, setSelectedObjectiveForPlan] = useState(null);
    const [editingPlan, setEditingPlan] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(6);

    // Fonctions de mapping
    const mapPriorityDisplay = (priority) => {
        const priorityMapping = {
            'high': {
                label: 'Haute',
                color: 'bg-red-100 text-red-800',
                dotColor: 'bg-red-500',
                order: 3
            },
            'medium': {
                label: 'Moyenne',
                color: 'bg-blue-100 text-blue-800',
                dotColor: 'bg-blue-500',
                order: 2
            },
            'low': {
                label: 'Basse',
                color: 'bg-gray-100 text-gray-800',
                dotColor: 'bg-gray-400',
                order: 1
            }
        };

        return priorityMapping[priority] || priorityMapping['medium'];
    };

    const mapCategoryForFilter = (dbCategory) => {
        const categoryMapping = {
            'Patrimoine Immobilier': 'patrimoine-immobilier',
            'Cash Flow Immobilier': 'revenus-locatifs',
            'Compétences & Stratégies': 'strategies-immobilieres',
            'Réseau & Visibilité': 'reseau-visibilite'
        };
        return categoryMapping[dbCategory] || dbCategory || 'autres';
    };

    // Transformer les données d'objectifs
    const gapData = useMemo(() => {
        if (!objectifs?.length) return [];

        return objectifs.map((objectif) => {
            if (!objectif.id_objectif) return null;

            const objectifPriority = objectif.priority || 'medium';
            const priorityData = mapPriorityDisplay(objectifPriority);

            return {
                id_objectif: objectif.id_objectif,
                category: objectif.category || 'Non catégorisé',
                categoryFilter: mapCategoryForFilter(objectif.category),
                title: objectif.title || 'Objectif sans titre',
                current: objectif.current_value || 0,
                target: objectif.target_value || 0,
                timeline: `${objectif.timeline || 0} ${objectif.timeline_unit || 'mois'}`,
                confidence: objectif.confidence || 70,
                trend: objectif.trend || 'up',
                icon: objectif.icon || 'Target',
                iconBgColor: objectif.icon_bg_color || 'bg-gray-50',
                iconColor: objectif.icon_color || 'text-gray-600',
                progressBarColor: objectif.progress_bar_color || '#016666',
                progress: objectif.progress_percentage || 0,
                status: objectif.status || 'active',
                value_unit: objectif.value_unit || '',
                value_type: objectif.value_type || 'number',
                priority: objectifPriority,
                priorityLabel: priorityData.label,
                priorityColor: priorityData.color,
                priorityDotColor: priorityData.dotColor,
                priorityOrder: priorityData.order,
                _original: objectif
            };
        }).filter(Boolean);
    }, [objectifs]);

    // Filtrage et tri
    const filteredData = useMemo(() => {
        let filtered = [...gapData];

        if (categoryFilter !== 'all') {
            filtered = filtered.filter(item => item.categoryFilter === categoryFilter);
        }

        if (priorityFilter !== 'all') {
            filtered = filtered.filter(item => item.priority === priorityFilter);
        }

        if (filterBy !== 'all') {
            filtered = filtered.filter(item => {
                if (filterBy === 'high') return item.confidence >= 80;
                if (filterBy === 'medium') return item.confidence >= 60 && item.confidence < 80;
                if (filterBy === 'low') return item.confidence < 60;
                if (filterBy === 'active') return item.status === 'active';
                if (filterBy === 'completed') return item.status === 'completed';
                if (filterBy === 'paused') return item.status === 'paused';
                return true;
            });
        }

        filtered.sort((a, b) => {
            let aValue, bValue;
            switch (sortBy) {
                case 'category':
                    aValue = a.category;
                    bValue = b.category;
                    break;
                case 'confidence':
                    aValue = a.confidence;
                    bValue = b.confidence;
                    break;
                case 'progress':
                    aValue = a.progress;
                    bValue = b.progress;
                    break;
                case 'timeline':
                    aValue = parseInt(a.timeline);
                    bValue = parseInt(b.timeline);
                    break;
                case 'priority':
                    aValue = a.priorityOrder;
                    bValue = b.priorityOrder;
                    break;
                default:
                    return 0;
            }

            if (typeof aValue === 'string') {
                return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            } else {
                return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
            }
        });

        return filtered;
    }, [gapData, filterBy, categoryFilter, priorityFilter, sortBy, sortOrder]);

    // Pagination
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterBy, categoryFilter, priorityFilter, sortBy, sortOrder]);

    // Handlers pour les actions
    const handleSaveObjective = async (objectiveData) => {
        try {
            let result;
            
            if (editingObjective) {
                result = await onUpdateObjective(editingObjective.id_objectif, {
                    title: objectiveData.title,
                    description: objectiveData.description || '',
                    category: objectiveData.category,
                    current_value: parseFloat(objectiveData.current_value) || 0,
                    target_value: parseFloat(objectiveData.target_value),
                    value_unit: objectiveData.value_unit || '',
                    priority: objectiveData.priority || 'medium',
                    timeline: parseInt(objectiveData.timeline) || 12,
                    timeline_unit: objectiveData.timeline_unit || 'mois',
                    confidence: parseInt(objectiveData.confidence) || 70
                });
            } else {
                result = await onCreateObjective({
                    title: objectiveData.title,
                    description: objectiveData.description || '',
                    category: objectiveData.category,
                    current_value: parseFloat(objectiveData.current_value) || 0,
                    target_value: parseFloat(objectiveData.target_value),
                    value_unit: objectiveData.value_unit || '',
                    priority: objectiveData.priority || 'medium',
                    timeline: parseInt(objectiveData.timeline) || 12,
                    timeline_unit: objectiveData.timeline_unit || 'mois',
                    confidence: parseInt(objectiveData.confidence) || 70
                });
            }

            if (result.success) {
                setShowModal(false);
                setEditingObjective(null);
            } else {
                alert(`Erreur lors de la sauvegarde: ${result.error}`);
            }
        } catch (error) {
            alert(`Erreur lors de la sauvegarde: ${error.message}`);
        }
    };

    const handleEditObjective = (objective) => {
        setEditingObjective(objective._original);
        setShowModal(true);
    };

    const handleDeleteObjective = async (objective) => {
        const objectiveId = objective.id_objectif;
        if (!objectiveId) {
            alert('Erreur: ID objectif manquant');
            return;
        }

        if (window.confirm(`Supprimer "${objective.title}" ?`)) {
            try {
                const result = await onDeleteObjective(objectiveId);
                
                if (!result.success) {
                    alert(`Erreur lors de la suppression: ${result.error}`);
                }
            } catch (error) {
                alert(`Erreur lors de la suppression: ${error.message}`);
            }
        }
    };

    const handleCreateActionPlan = async (objective) => {
        const objectiveForPlan = objective._original || objective;

        try {
            const existingPlans = plans.filter(plan =>
                plan.id_objectif === objectiveForPlan.id_objectif
            );

            if (existingPlans.length > 0) {
                const planToEdit = existingPlans[0];
                setSelectedObjectiveForPlan(objectiveForPlan);
                setEditingPlan(planToEdit);
                setShowActionPlanModal(true);
            } else {
                setSelectedObjectiveForPlan(objectiveForPlan);
                setEditingPlan(null);
                setShowActionPlanModal(true);
            }
        } catch (error) {
            setSelectedObjectiveForPlan(objectiveForPlan);
            setEditingPlan(null);
            setShowActionPlanModal(true);
        }
    };

    const handleSaveActionPlan = async () => {
        try {
            setShowActionPlanModal(false);
            setSelectedObjectiveForPlan(null);
            setEditingPlan(null);
            
            if (onSmartRefresh) {
                onSmartRefresh('plan_created');
            } else if (onGlobalRefresh) {
                onGlobalRefresh();
            }
        } catch (error) {
            // Erreur silencieuse
        }
    };

    const handleResetFilters = () => {
        setSortBy('category');
        setSortOrder('asc');
        setFilterBy('all');
        setCategoryFilter('all');
        setPriorityFilter('all');
    };

    const handleManualRefresh = async () => {
        try {
            if (onSmartRefresh) {
                onSmartRefresh('manual_refresh');
            } else if (onGlobalRefresh) {
                onGlobalRefresh();
            }
        } catch (error) {
            // Erreur silencieuse
        }
    };

    // États de chargement
    if (isLoading && !objectifs.length) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
                <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 -m-4 mb-4 flex-shrink-0 rounded-t-xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-700">
                            <div className="w-6 h-6 bg-gray-200 rounded-lg flex items-center justify-center mr-2">
                                <Target className="w-3.5 h-3.5 text-gray-600" />
                            </div>
                            <h2 className="text-sm font-semibold">Gestion des objectifs</h2>
                        </div>
                    </div>
                </div>

                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#016666] mx-auto mb-4"></div>
                        <p className="text-gray-500">Chargement des objectifs...</p>
                    </div>
                </div>
            </div>
        );
    }

    // États d'erreur
    if (hasError && !objectifs.length) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
                <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 -m-4 mb-4 flex-shrink-0 rounded-t-xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center text-gray-700">
                            <h2 className="text-sm font-semibold">Gestion des objectifs</h2>
                        </div>
                    </div>
                </div>

                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-red-500 mb-4">⚠️</div>
                        <p className="text-gray-900 font-medium mb-2">Erreur de chargement</p>
                        <div className="text-gray-500 text-sm mb-4">
                            {Object.entries(errors)
                                .filter(([, error]) => error)
                                .map(([type, error]) => (
                                    <div key={type}>{type}: {error.message || error}</div>
                                ))
                            }
                        </div>
                        <button
                            onClick={handleManualRefresh}
                            className="px-4 py-2 bg-[#016666] text-white rounded-lg hover:bg-[#014d4d]"
                        >
                            Réessayer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-full flex flex-col">
            {/* Header */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 -m-4 mb-4 flex-shrink-0 rounded-t-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center text-gray-700">
                        <h2 className="text-sm font-semibold">Gestion des objectifs</h2>
                        {isLoading && (
                            <RefreshCw className="w-3 h-3 ml-2 animate-spin text-gray-500" />
                        )}
                    </div>
                    <div className="flex items-center space-x-3">
                        {/* Vue toggle */}
                        <div className="flex items-center bg-gray-200 rounded-md p-1">
                            <button
                                onClick={() => setViewMode('card')}
                                className={`p-1.5 rounded ${viewMode === 'card' ? 'bg-white shadow-sm' : ''}`}
                            >
                                <Grid3X3 className="w-3 h-3 text-gray-500" />
                            </button>
                            <button
                                onClick={() => setViewMode('table')}
                                className={`p-1.5 rounded ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}
                            >
                                <List className="w-3 h-3 text-gray-500" />
                            </button>
                        </div>

                        {/* Tri */}
                        <button
                            onClick={() => { setShowSort(!showSort); setShowFilters(false); }}
                            className={`flex items-center px-2 py-1 rounded-md text-xs ${showSort ? 'bg-gray-200' : 'hover:bg-gray-200'}`}
                        >
                            <ArrowUpDown className="w-3 h-3 mr-1" />
                            Trier
                        </button>

                        {/* Filtres */}
                        <button
                            onClick={() => { setShowFilters(!showFilters); setShowSort(false); }}
                            className={`flex items-center px-2 py-1 rounded-md text-xs ${showFilters ? 'bg-gray-200' : 'hover:bg-gray-200'}`}
                        >
                            <Filter className="w-3 h-3 mr-1" />
                            Filtrer
                            {(filterBy !== 'all' || categoryFilter !== 'all' || priorityFilter !== 'all') && (
                                <span className="ml-1 w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                            )}
                        </button>

                        {/* Ajouter */}
                        <button
                            onClick={() => { setEditingObjective(null); setShowModal(true); }}
                            className="px-3 py-1 bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm hover:shadow-md flex items-center text-sm"
                        >
                            <Plus className="w-3 h-3 mr-1" />
                            Ajouter
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtres */}
            {(showSort || showFilters) && (
                <div className="flex-shrink-0">
                    <GapAnalysisFilters
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        sortOrder={sortOrder}
                        setSortOrder={setSortOrder}
                        filterBy={filterBy}
                        setFilterBy={setFilterBy}
                        categoryFilter={categoryFilter}
                        setCategoryFilter={setCategoryFilter}
                        priorityFilter={priorityFilter}
                        setPriorityFilter={setPriorityFilter}
                        onReset={handleResetFilters}
                        showOnlySort={showSort}
                        showOnlyFilter={showFilters}
                    />
                </div>
            )}

            {/* Content */}
            <div className="flex-1 flex flex-col min-h-0 mb-4">
                {filteredData.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                            <div className="text-lg font-medium mb-2">
                                {gapData.length === 0 ? 'Aucun objectif' : 'Aucun objectif trouvé'}
                            </div>
                            <div className="text-sm">
                                {gapData.length === 0 ? 'Commencez par ajouter votre premier objectif' : 'Essayez de modifier vos filtres'}
                            </div>
                            {gapData.length > 0 && (filterBy !== 'all' || categoryFilter !== 'all' || priorityFilter !== 'all') && (
                                <button
                                    onClick={handleResetFilters}
                                    className="mt-3 px-4 py-2 text-sm bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors"
                                >
                                    Réinitialiser les filtres
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <GapAnalysisItems
                        data={paginatedData}
                        viewMode={viewMode}
                        onEdit={handleEditObjective}
                        onDelete={handleDeleteObjective}
                        onCreateActionPlan={handleCreateActionPlan}
                    />
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex-shrink-0 border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center">
                        <button
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 text-sm bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
                        >
                            Précédent
                        </button>
                        <div className="flex items-center space-x-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                                <button
                                    key={pageNumber}
                                    onClick={() => setCurrentPage(pageNumber)}
                                    className={`px-3 py-1.5 text-sm rounded-md ${currentPage === pageNumber ? 'bg-[#016666] text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                                >
                                    {pageNumber}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1.5 text-sm bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
                        >
                            Suivant
                        </button>
                    </div>
                </div>
            )}

            {/* Modals */}
            <ObjectivesModal
                isOpen={showModal}
                onClose={() => { setShowModal(false); setEditingObjective(null); }}
                onSave={handleSaveObjective}
                editObjectif={editingObjective}
            />

            <ActionPlanModal
                isOpen={showActionPlanModal}
                onClose={() => {
                    setShowActionPlanModal(false);
                    setSelectedObjectiveForPlan(null);
                    setEditingPlan(null);
                }}
                onSave={handleSaveActionPlan}
                objective={selectedObjectiveForPlan}
                editPlan={editingPlan}
                plansCachedHook={plansCachedHook}
            />
        </div>
    );
};

export default GapAnalysisSection;